import React, { useRef, useState } from "react";
import { Box, Flex, Text, Spacer, IconButton } from "@chakra-ui/react";
import { AiOutlinePlus, AiOutlineClose, AiOutlineMenu } from "react-icons/ai";

// Two-pane transfer list: left = available columns (click / + to add),
// right = selected columns (× to remove, drag rows to reorder). `items` is the
// full master list; `selected` is the chosen subset in display order.
function ColumnTransfer({ items = [], selected = [], onChange }) {
  const dragIndex = useRef(null);
  const [overIndex, setOverIndex] = useState(null);

  const selectedValues = new Set(selected.map((s) => s.value));
  const available = items.filter((i) => !selectedValues.has(i.value));

  const add = (item) => onChange([...selected, item]);
  const remove = (value) => onChange(selected.filter((s) => s.value !== value));

  const reorder = (from, to) => {
    if (from === null || to === null || from === to) return;
    const next = [...selected];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const Panel = ({ title, count, children, mr }) => (
    <Box flex="1" mr={mr} borderWidth="1px" borderRadius="md" overflow="hidden">
      <Flex bg="gray.50" px={3} py={2} alignItems="center" borderBottomWidth="1px">
        <Text fontSize="13px" fontWeight="600">{title}</Text>
        <Spacer />
        <Text fontSize="12px" color="gray.500">{count}</Text>
      </Flex>
      <Box maxH="340px" minH="120px" overflowY="auto" p={1}>{children}</Box>
    </Box>
  );

  return (
    <Flex mb={3} align="stretch">
      <Panel title="Available columns" count={available.length} mr={3}>
        {available.map((item) => (
          <Flex
            key={item.value}
            px={2}
            py={1}
            borderRadius="md"
            alignItems="center"
            cursor="pointer"
            _hover={{ bg: "gray.100" }}
            onClick={() => add(item)}
          >
            <Text fontSize="13px">{item.label || item.value}</Text>
            <Spacer />
            <Box as={AiOutlinePlus} color="teal.500" />
          </Flex>
        ))}
        {available.length === 0 && (
          <Text fontSize="12px" color="gray.400" p={2}>All columns selected</Text>
        )}
      </Panel>
      <Panel title="Selected columns (drag to reorder)" count={selected.length}>
        {selected.map((item, idx) => (
          <Flex
            key={item.value}
            px={2}
            py={1}
            borderRadius="md"
            alignItems="center"
            draggable
            onDragStart={(e) => {
              dragIndex.current = idx;
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", String(idx)); // Firefox needs data set
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (overIndex !== idx) setOverIndex(idx);
            }}
            onDrop={() => {
              reorder(dragIndex.current, idx);
              dragIndex.current = null;
              setOverIndex(null);
            }}
            onDragEnd={() => {
              dragIndex.current = null;
              setOverIndex(null);
            }}
            cursor="grab"
            bg={overIndex === idx ? "teal.50" : "transparent"}
            borderTopWidth={overIndex === idx ? "2px" : "0px"}
            borderTopColor="teal.300"
            _hover={{ bg: overIndex === idx ? "teal.50" : "gray.50" }}
          >
            <Box as={AiOutlineMenu} mr={2} color="gray.400" />
            <Text fontSize="13px">{item.label || item.value}</Text>
            <Spacer />
            <IconButton
              aria-label="Remove"
              size="xs"
              variant="ghost"
              colorScheme="red"
              icon={<AiOutlineClose />}
              onClick={() => remove(item.value)}
            />
          </Flex>
        ))}
        {selected.length === 0 && (
          <Text fontSize="12px" color="gray.400" p={2}>No columns selected</Text>
        )}
      </Panel>
    </Flex>
  );
}

export default ColumnTransfer;
